import { Component, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { LangPipe, LangService } from '@reforgium/presentia';
import {
  SkButtonComponent,
  SkDynamicDialogService,
  SkFileUploadComponent,
  SkFormFieldComponent,
  SkInputTextComponent,
  SkSelectComponent,
} from '@ss/ui';

import { AppNotificationService } from 'services/app-notification.service';

import { DocumentFileData, DocumentLangs } from 'entities/document.model';
import { DictService } from 'use-cases/dict.service';
import { DocumentService } from 'use-cases/document.service';

type UploadFileType = DocumentFileData['fileType'];
type UploadFileFormat = DocumentFileData['format'];

@Component({
  selector: 'app-upload-dialog',
  template: `
    <form class="upload-form" (ngSubmit)="upload()">
      <sk-file-upload
        [accept]="fileTypes"
        [multiple]="false"
        [chooseLabel]="'administrative.upload.select' | lang"
        (filesSelected)="selectFile($event)"
      />

      <sk-field [label]="'administrative.upload.title' | lang">
        <sk-input-text [(value)]="name" [readonly]="true" [placeholder]="'administrative.upload.title' | lang" />
      </sk-field>

      <sk-field [label]="'administrative.upload.lang' | lang" required>
        <sk-select
          [(value)]="language"
          [options]="dict.lang.options()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="'administrative.placeholders.select' | lang"
          [ariaLabel]="'administrative.upload.lang' | lang"
          (changed)="languageDirty.set(true)"
        />
      </sk-field>

      <sk-field [label]="'administrative.upload.fileType' | lang" [required]="true">
        <sk-select
          [(value)]="fileType"
          [options]="dict.fileTypes.options()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="'administrative.placeholders.select' | lang"
          [ariaLabel]="'administrative.upload.fileType' | lang"
        />
      </sk-field>

      <sk-field [label]="'administrative.upload.format' | lang" [required]="true">
        <sk-select
          [(value)]="format"
          [options]="dict.fileFormats.options()"
          optionLabel="label"
          optionValue="value"
          [placeholder]="'administrative.placeholders.select' | lang"
          [ariaLabel]="'administrative.upload.format' | lang"
        />
      </sk-field>

      <footer class="upload-actions">
        <sk-button type="button" appearance="secondary" [label]="'administrative.close' | lang" (clicked)="close()" />
        <sk-button type="submit" appearance="primary" [label]="'administrative.upload.action' | lang" />
      </footer>
    </form>
  `,
  styles: `
    .upload-form {
      display: grid;
      gap: 1rem;
      padding-top: 1rem;
    }

    .upload-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }
  `,
  providers: [DocumentService],
  imports: [
    LangPipe,
    SkButtonComponent,
    SkFileUploadComponent,
    SkFormFieldComponent,
    SkInputTextComponent,
    SkSelectComponent,
    FormsModule,
  ],
})
export class UploadDialog {
  versionId = input<number>();

  protected name = signal('');
  protected language = signal<DocumentLangs | null>(null);
  protected fileType = signal<UploadFileType>('ORIGINAL');
  protected format = signal<UploadFileFormat | null>(null);
  protected languageDirty = signal(false);
  protected file = signal<File | null>(null);

  public langService = inject(LangService);
  public notification = inject(AppNotificationService);
  public dict = inject(DictService);
  public document = inject(DocumentService);
  private readonly dialog = inject(SkDynamicDialogService);

  readonly fileTypes =
    '.pdf,.docx,.doc,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  constructor() {
    effect(() => {
      const lang = this.langService.currentLang();

      if (!this.languageDirty()) {
        untracked(() => this.language.set(lang.toUpperCase() as DocumentLangs));
      }
    });
  }

  protected selectFile(files: readonly File[]): void {
    const file = files[0] ?? null;

    this.file.set(file);

    if (!file) {
      this.name.set('');
      this.format.set(null);

      return;
    }

    const [ext, ...chunks] = file.name.split('.').reverse();

    this.name.set(chunks.reverse().join('.'));
    this.format.set(ext.toUpperCase() as UploadFileFormat);
  }

  protected async upload(): Promise<void> {
    const versionId = this.versionId();
    const file = this.file();
    const language = this.language();
    const fileType = this.fileType();
    const format = this.format();

    if (!file || !versionId || !language || !fileType || !format) {
      return;
    }

    const fileData: DocumentFileData = { fileType, format, language };

    await this.document.upload(versionId, fileData, file).then(() => {
      this.notification.info('administrative.notifications.upload', { translate: true });
      this.close();
    });
  }

  protected close(): void {
    this.dialog.close();
  }
}
